import { EllipsisBox } from '@/components/molecules/text/EllipsisBox'
import { useTime } from '@/core/hooks/useTime'
import { useChatMessages } from '@/entity/chat/useChatMessages'
import { useChatRoomDocument } from '@/entity/chat/useChatRoomDocument'
import { useUserAccount } from '@/entity/user/useUserAccount'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { AlertState, UserAlert } from '@rallycry/social-api-typescript'
import { last } from 'lodash-es'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { ChatMessageText } from './ChatMessageText'

export const ChatListRow = ({
  id,
  showChat,
  alert
}: {
  id: string
  showChat: (id: string) => any
  button?: boolean
  alert?: UserAlert
}) => {
  const { user } = useUserAccount()
  const { getNow } = useTime()
  const { room } = useChatRoomDocument({ idOrKey: id })
  const { messages } = useChatMessages({
    idOrKey: id,
    limit: 1
  })
  const [currentTime, setCurrentTime] = useState(getNow())

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getNow())
    }, 15000) // Update every 15 seconds

    return () => clearInterval(interval)
  }, [getNow])

  const lastMessage = last(messages)
  const lastMessageAgo = moment.duration(
    currentTime.diff(moment(lastMessage?.dateCreated))
  )

  const timeDisplay =
    lastMessage && !room?.archived
      ? lastMessageAgo.asDays() >= 1
        ? `${Math.floor(lastMessageAgo.asDays())}d`
        : lastMessageAgo.asHours() >= 1
          ? `${Math.floor(lastMessageAgo.asHours())}h`
          : `${Math.round(lastMessageAgo.asMinutes()) + 1}m`
      : null

  return (
    <ListItemButton sx={{ height: 64 }} onClick={() => showChat(id)}>
      <ListItem
        disableGutters
        secondaryAction={
          <Stack
            direction='column'
            justifyContent={'flex-start'}
            alignItems={'flex-end'}
            spacing={2}
          >
            <Typography variant='subtitle2' color='text.secondary'>
              {timeDisplay}
            </Typography>
            {/* notification circle */}
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor:
                  alert?.state === AlertState.HAS_LOUD
                    ? 'red'
                    : alert?.state === AlertState.HAS_QUIET
                      ? 'white'
                      : 'transparent'
              }}
            />
          </Stack>
        }
      >
        <ListItemAvatar>
          <Avatar src={room?.image} variant='rounded' />
        </ListItemAvatar>

        <Stack direction='column' spacing={0.5} width={'100%'}>
          <EllipsisBox lineClamp={1}>
            <Typography variant={'h3'} color='text.primary'>
              {room?.name || id}
            </Typography>
          </EllipsisBox>

          <EllipsisBox lineClamp={1}>
            <Typography variant='subtitle2' color='text.secondary'>
              <ChatMessageText
                key={lastMessage?.uid}
                message={lastMessage}
                textOnly
                tagSelf={Number(lastMessage?.author) === user?.id}
              />
            </Typography>
          </EllipsisBox>
        </Stack>
      </ListItem>
    </ListItemButton>
  )
}
